import type { LogsEvent } from '@datadog/browser-logs';
import { datadogLogs } from '@datadog/browser-logs';

import { HTTPError } from '~/shared/http-fetcher/http-error';
import { NetworkError } from '~/shared/http-fetcher/network-error';
import type { ErrorReporter } from '~/shared/validating-http-fetcher/validating-http-fetcher';

type DebugBlockedEndpoint = {
    readonly errorMessage?: string;
    readonly id: string;
    readonly label: string;
    readonly url: string;
};

type DebugBlockedEndpointsEvent = {
    readonly appVersion: string;
    readonly blockedEndpoints: readonly DebugBlockedEndpoint[];
    readonly checkedAt: string;
};

const isNetworkErrorEvent = (event: LogsEvent): boolean =>
    event.error?.kind === 'NetworkError' || event.error?.stack?.startsWith('NetworkError:') === true;

/**
 * The query cache reports a terminal network failure once, through the logger. React's error
 * boundary then console.errors the same error and the SDK forwards that copy too, so only the
 * console channel is dropped. An uncaught network error (origin `source`) is kept: nothing
 * else reports the paths the query client does not own.
 */
export const shouldSendLogEvent = (event: LogsEvent): boolean => {
    if (event.error?.stack?.startsWith('AbortError:') === true) {
        return false;
    }

    if (event.http?.status_code === 0) {
        return false;
    }

    if (event.origin === 'console' && isNetworkErrorEvent(event)) {
        return false;
    }

    return true;
};

export const initDataDog = (): ErrorReporter => {
    datadogLogs.init({
        beforeSend: shouldSendLogEvent,
        clientToken: 'pubb709d48d6e6fd56a010dda5934c4be1d',
        env: import.meta.env.PROD === true ? 'prod' : 'dev',
        forwardConsoleLogs: ['error'],
        forwardErrorsToLogs: true,
        proxy: import.meta.env.DATADOG_PROXY_URL,
        service: 'frontend',
        sessionSampleRate: 100,
        site: 'datadoghq.eu',
        version: import.meta.env.APP_VERSION,
    });

    datadogLogs.setGlobalContextProperty('tenant_hostnameSlug', globalThis.location.hostname);

    return {
        reportError: (message, error, context) => {
            if (error instanceof HTTPError) {
                datadogLogs.logger.error(
                    message,
                    {
                        ...context,
                        http: {
                            method: error.request.method,
                            status_code: error.response.status,
                            url: error.request.url.toString(),
                        },
                    },
                    error,
                );
            } else if (error instanceof NetworkError) {
                // No status_code: the request never produced a response. The http block keeps
                // the log searchable by endpoint alongside the readable HTTP failures.
                datadogLogs.logger.error(
                    message,
                    { ...context, http: { method: error.method, url: error.url } },
                    error,
                );
            } else {
                datadogLogs.logger.error(message, context, error);
            }
        },
    };
};

export const setDatadogTenantId = (tenantId: string): void => {
    datadogLogs.setGlobalContextProperty('tenant_id', tenantId);
};

export const reportRecoveredRichEditorError = (context: Readonly<Record<string, unknown>>, error: Error): void => {
    datadogLogs.logger.warn('Rich editor recovered from Lexical error', { richEditor: context }, error);
};

export const reportDebugBlockedEndpoints = (event: DebugBlockedEndpointsEvent): void => {
    datadogLogs.logger.error('Debug endpoint blocked', {
        debug: event,
        eventName: 'debug_endpoint_blocked',
    });
};

type DebugStreamFailedEvent = {
    readonly appVersion: string;
    readonly expectedEvents: number;
    readonly receivedEvents: number;
    readonly url: string;
};

export const reportDebugStreamFailed = (event: DebugStreamFailedEvent): void => {
    datadogLogs.logger.error('Debug stream check failed', {
        debug: event,
        eventName: 'debug_stream_failed',
    });
};

export type DroppedChatStreamEventIssue = {
    readonly code: string;
    readonly path: string;
};

export type DroppedChatStreamEventContext = {
    readonly eventType: string;
    readonly issues?: readonly DroppedChatStreamEventIssue[];
    readonly threadId?: string;
};

export const reportDroppedChatStreamEvent = (context: DroppedChatStreamEventContext): void => {
    datadogLogs.logger.warn('Chat stream event dropped: failed parsing or validation', {
        chat: context,
        eventName: 'chat_stream_event_dropped',
    });
};

const reportedDroppedChatHistoryItems = new Set<string>();

/** History is refetched while a thread runs, so each dropped item is reported once per page load. */
export const reportDroppedChatHistoryItem = (item: {
    readonly id?: string;
    readonly issues: readonly DroppedChatStreamEventIssue[];
    readonly kind: string;
}): void => {
    const key = `${item.kind}:${item.id ?? JSON.stringify(item.issues)}`;
    if (reportedDroppedChatHistoryItems.has(key)) return;
    reportedDroppedChatHistoryItems.add(key);
    datadogLogs.logger.warn('Chat history item dropped: failed parsing or validation', {
        chat: item,
        eventName: 'chat_history_item_dropped',
    });
};
